import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import React, { createContext, type ReactNode, useContext } from "react";
import { convex } from "@/lib/convexClient";
import { useDarkMode } from "@/lib/cookies";
import { queryClient } from "@/lib/queryClient";
import "@/components/_shared/styles/globals.css";
import "@/components/_shared/styles/animations.css"; // Import animations for background

// Dark Mode Context (moved here for global layout scope)
export const DarkModeContext = createContext<{
	darkMode: boolean;
	toggleDarkMode: () => void;
}>({
	darkMode: true, // Default value
	toggleDarkMode: () => {},
});

// Hook for easy access (remains the same)
export const useGlobalDarkMode = () => useContext(DarkModeContext);

// Root Layout Component
function RootLayoutContent({ children }: { children: ReactNode }) {
	const darkModeState = useDarkMode(); // Initialize dark mode state here

	return (
		<DarkModeContext.Provider value={darkModeState}>
			<div
				className={
					"animated-gradient-bg relative min-h-screen w-full bg-linear-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950"
				}
			>
				{/* Animierter Hintergrund */}
				<div className="pointer-events-none absolute inset-0 overflow-hidden">
					<div className="blob blob-indigo" />
					<div className="blob blob-violet" />
					<div className="pulsating-dot dot-1" />
					<div className="pulsating-dot dot-2" />
				</div>
				{/* Relative container for content */}
				<div className="relative z-10">{children}</div>
			</div>
		</DarkModeContext.Provider>
	);
}

export default function RootLayout({ children }: { children: ReactNode }) {
	const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

	if (!publishableKey) {
		throw new Error("Missing Clerk Publishable Key");
	}

	// In a SPA environment (e.g., Vite + wouter), React mounts into an existing <div id="root">.
	// Rendering <html> / <head> inside the React tree would nest <html> under a <div>, causing warnings.
	// Keep providers at the top level and let index.html own <html>/<head>/<body>.
	return (
		<ClerkProvider publishableKey={publishableKey}>
			<ConvexProviderWithClerk client={convex} useAuth={useAuth}>
				<QueryClientProvider client={queryClient}>
					<RootLayoutContent>{children}</RootLayoutContent>
				</QueryClientProvider>
			</ConvexProviderWithClerk>
		</ClerkProvider>
	);
}
